import { randomUUID } from "node:crypto";

import { AGY_CODE_ASSIST_ENDPOINT } from "../../constants";
import modelsJson from "../../../models.json";
import { normalizeThinkingConfig } from "../request-helpers";
import { buildAgyCliUserAgent } from "../user-agent";
import { normalizeRequestPayloadIdentifiers, normalizeWrappedIdentifiers } from "./identifiers";
import { addThoughtSignaturesToFunctionCalls, transformOpenAIToolCalls } from "./openai";
import { isGenerativeLanguageRequest, parseGenerativeLanguageRequest } from "./shared";
import { getLatestSignature } from "../../plugin/cache";
import { closeToolLoopForThinking } from "./thinking";
import { getTurnStateTracker } from "./turn-state-tracker";
import { getToolMapper, sanitizeToolName, type ToolMapper } from "./tool-mapper";

const STREAM_ACTION = "streamGenerateContent";

export interface ThinkingConfigDefaults {
  provider?: unknown;
  models?: Record<string, unknown>;
}

/**
 * Rewrites OpenAI-style requests into the format for Gemini Code Assist requests.
 */
export function prepareAgyRequest(
  input: RequestInfo,
  init: RequestInit | undefined,
  accessToken: string,
  projectId: string,
  thinkingConfigDefaults?: ThinkingConfigDefaults,
): {
  request: RequestInfo;
  init: RequestInit;
  streaming: boolean;
  requestedModel?: string;
  sessionId?: string;
} {
  const baseInit: RequestInit = { ...init };
  const headers = new Headers(init?.headers ?? {});

  if (!isGenerativeLanguageRequest(input)) {
    return {
      request: input,
      init: { ...baseInit, headers },
      streaming: false,
    };
  }

  const requestTarget = parseGenerativeLanguageRequest(input);
  if (!requestTarget) {
    return {
      request: input,
      init: { ...baseInit, headers },
      streaming: false,
    };
  }

  headers.set("Authorization", `Bearer ${accessToken}`);
  headers.delete("x-api-key");
  headers.delete("x-goog-api-key");

  const { requestedModel: rawModel, effectiveModel, action: rawAction } = requestTarget;
  const streaming = rawAction === STREAM_ACTION;

  const transformedUrl = `${AGY_CODE_ASSIST_ENDPOINT}/v1internal:${rawAction}${
    streaming ? "?alt=sse" : ""
  }`;

  let body = baseInit.body;
  let sessionId: string | undefined;

  if (typeof baseInit.body === "string" && baseInit.body) {
    const transformed = transformRequestBody(
      baseInit.body,
      projectId,
      effectiveModel,
      rawModel,
      thinkingConfigDefaults,
    );
    if (transformed.body) {
      body = transformed.body;
    }
    sessionId = transformed.sessionId;
  }

  if (streaming) {
    headers.set("Accept", "text/event-stream");
  }

  const userAgent = buildAgyCliUserAgent(effectiveModel);
  headers.set("User-Agent", userAgent);

  return {
    request: transformedUrl,
    init: {
      ...baseInit,
      headers,
      body,
    },
    streaming,
    requestedModel: rawModel,
    sessionId,
  };
}

function getModelEnum(modelName: string): string {
  const deprecated = (modelsJson as any).deprecatedModelIds;
  if (deprecated && deprecated[modelName] && deprecated[modelName].newModelEnum) {
    return deprecated[modelName].newModelEnum;
  }
  const models = (modelsJson as any).models;
  if (models && models[modelName] && models[modelName].model) {
    return models[modelName].model;
  }
  if (deprecated && deprecated[modelName] && deprecated[modelName].oldModelEnum) {
    return deprecated[modelName].oldModelEnum;
  }
  return "MODEL_PLACEHOLDER_M16";
}

function transformRequestBody(
  body: string,
  projectId: string,
  effectiveModel: string,
  requestedModel: string,
  thinkingConfigDefaults?: ThinkingConfigDefaults,
): { body?: string; userPromptId: string; sessionId?: string } {
  const fallbackId = randomUUID();
  try {
    const parsedBody = JSON.parse(body) as Record<string, unknown>;
    const isWrapped = typeof parsedBody.project === "string" && "request" in parsedBody;

    if (isWrapped) {
      const wrappedBody = {
        ...parsedBody,
        model: effectiveModel,
      } as Record<string, unknown>;

      const wrappedModel = (wrappedBody.model as string) || "";
      if (wrappedModel.includes("-image") && !wrappedBody.requestType) {
        wrappedBody.requestType = "image_gen";
        wrappedBody.userAgent = wrappedBody.userAgent || "antigravity";
      }

      const { userPromptId, sessionId, requestId } = normalizeWrappedIdentifiers(wrappedBody);
      const toolMapper = getToolMapper(sessionId);

      const requestPayloadInside = wrappedBody.request as Record<string, unknown> | undefined;
      if (requestPayloadInside) {
        toolMapper.registerFromFunctionDeclarations(requestPayloadInside.tools);
        toolMapper.registerFromContents(requestPayloadInside.contents);

        normalizeThinking(
          requestPayloadInside,
          resolveDefaultThinkingConfig(thinkingConfigDefaults, requestedModel, effectiveModel),
          thinkingConfigDefaults?.provider,
        );
      }
      if (requestPayloadInside && !requestPayloadInside.labels) {
        requestPayloadInside.labels = {
          last_execution_id: randomUUID(),
          last_step_index: "0",
          model_enum: getModelEnum(effectiveModel),
          trajectory_id: randomUUID(),
          used_claude: "false",
          used_claude_conservative: "false"
        };
      }

      if (requestPayloadInside && Array.isArray(requestPayloadInside.tools)) {
        normalizeToolSchemaTypes(requestPayloadInside.tools, toolMapper);
      }
      if (requestPayloadInside) {
        normalizeToolConfig(requestPayloadInside, toolMapper);
      }
      if (requestPayloadInside && Array.isArray(requestPayloadInside.contents)) {
        let contents = requestPayloadInside.contents;

        normalizeToolNamesInContents(contents, toolMapper);
        injectMissingToolCallIds(contents);
        fixOrphanedFunctionResponses(contents);

        const tracker = getTurnStateTracker();
        let needsRecovery = false;
        if (sessionId && tracker) {
          const state = tracker.getState(sessionId) ?? tracker.recoverFromContents(sessionId, contents);
          needsRecovery = state.inToolLoop && !state.turnHasThinking;
        }
        if (needsRecovery) {
          contents = closeToolLoopForThinking(contents);
        }

        contents = normalizeContentsSequence(contents);
        contents = ensureTrailingUserTurn(contents);

        const latestSig = getLatestSignature(sessionId);
        applyLatestSignature(contents, latestSig);
        requestPayloadInside.contents = contents;
      }

      return { body: JSON.stringify(wrappedBody), userPromptId, sessionId };
    }

    const requestPayload = { ...parsedBody };
    const { userPromptId, sessionId, requestId } = normalizeRequestPayloadIdentifiers(requestPayload);
    const toolMapper = getToolMapper(sessionId);

    toolMapper.registerFromOpenAITools(requestPayload.tools);
    toolMapper.registerFromFunctionDeclarations(requestPayload.tools);
    toolMapper.registerFromContents(requestPayload.contents);

    if (Array.isArray(requestPayload.tools)) {
      normalizeToolSchemaTypes(requestPayload.tools, toolMapper);
    }
    normalizeToolConfig(requestPayload, toolMapper);
    transformOpenAIToolCalls(requestPayload, toolMapper);
    addThoughtSignaturesToFunctionCalls(requestPayload);
    normalizeThinking(
      requestPayload,
      resolveDefaultThinkingConfig(thinkingConfigDefaults, requestedModel, effectiveModel),
      thinkingConfigDefaults?.provider,
    );
    normalizeSystemInstruction(requestPayload);
    normalizeCachedContent(requestPayload);

    let contents = requestPayload.contents as any[];
    if (Array.isArray(contents)) {
      normalizeToolNamesInContents(contents, toolMapper);
      injectMissingToolCallIds(contents);
      fixOrphanedFunctionResponses(contents);

      const tracker = getTurnStateTracker();
      let needsRecovery = false;
      if (sessionId && tracker) {
        const state = tracker.getState(sessionId) ?? tracker.recoverFromContents(sessionId, contents);
        needsRecovery = state.inToolLoop && !state.turnHasThinking;
      }
      if (needsRecovery) {
        contents = closeToolLoopForThinking(contents);
      }

      contents = normalizeContentsSequence(contents);
      contents = ensureTrailingUserTurn(contents);

      const latestSig = getLatestSignature(sessionId);
      applyLatestSignature(contents, latestSig);
      requestPayload.contents = contents;
    }

    if ("model" in requestPayload) {
      delete requestPayload.model;
    }

    if (!requestPayload.labels) {
      requestPayload.labels = {
        last_execution_id: randomUUID(),
        last_step_index: "0",
        model_enum: getModelEnum(effectiveModel),
        trajectory_id: randomUUID(),
        used_claude: "false",
        used_claude_conservative: "false"
      };
    }

    const isImageGen =
      effectiveModel.includes("-image") ||
      requestedModel.includes("-image") ||
      (typeof requestPayload.generationConfig === "object" &&
        requestPayload.generationConfig !== null &&
        "imageConfig" in (requestPayload.generationConfig as Record<string, unknown>));

    const wrappedBody: Record<string, unknown> = {
      project: projectId,
      model: effectiveModel,
      requestId,
      request: requestPayload,
      userAgent: "antigravity"
    };

    if (isImageGen) {
      wrappedBody.requestType = "image_gen";
    } else if (
      effectiveModel.includes("gemini-3.5-flash-lite") ||
      effectiveModel.includes("gemini-3.1-flash-lite")
    ) {
      wrappedBody.requestType = "checkpoint";
    } else if (effectiveModel.includes("gemini-2.5-flash-lite")) {
      wrappedBody.requestType = "chat";
    } else if (effectiveModel.includes("-lite")) {
      wrappedBody.requestType = "web_search";
    } else {
      wrappedBody.requestType = "agent";
    }

    return { body: JSON.stringify(wrappedBody), userPromptId, sessionId };
  } catch (error) {
    const errStr = error instanceof Error ? error.stack || error.message : String(error);
    console.warn(`[Agy Auth] Failed to transform Gemini request body: ${errStr}`);
    return { userPromptId: fallbackId };
  }
}

function resolveDefaultThinkingConfig(
  thinkingConfigDefaults: ThinkingConfigDefaults | undefined,
  requestedModel: string,
  effectiveModel: string,
): unknown {
  const configured = thinkingConfigDefaults?.models
    ? thinkingConfigDefaults.models[requestedModel] ?? thinkingConfigDefaults.models[effectiveModel]
    : undefined;
  if (configured !== undefined) {
    return configured;
  }

  return getImplicitThinkingConfigForModel(requestedModel) ?? getImplicitThinkingConfigForModel(effectiveModel);
}

function normalizeThinking(
  requestPayload: Record<string, unknown>,
  modelThinkingConfig: unknown,
  providerThinkingConfig: unknown,
): void {
  const rawGenerationConfig = isRecord(requestPayload.generationConfig)
    ? { ...requestPayload.generationConfig }
    : undefined;
  const mergedThinkingConfig = mergeThinkingConfigs(
    providerThinkingConfig,
    modelThinkingConfig,
    requestPayload.thinkingConfig,
    rawGenerationConfig?.thinkingConfig,
  );

  if (Object.prototype.hasOwnProperty.call(requestPayload, "thinkingConfig")) {
    delete requestPayload.thinkingConfig;
  }

  const normalizedThinkingConfig = normalizeThinkingConfig(mergedThinkingConfig);

  if (!normalizedThinkingConfig) {
    if (rawGenerationConfig) {
      requestPayload.generationConfig = rawGenerationConfig;
    }
    return;
  }

  requestPayload.generationConfig = {
    ...(rawGenerationConfig ?? {}),
    thinkingConfig: normalizedThinkingConfig,
  };
}

function getImplicitThinkingConfigForModel(modelId: string): unknown {
  const normalizedModelId = modelId.toLowerCase();
  if (!normalizedModelId.startsWith("gemini-") || normalizedModelId.includes("image")) {
    return undefined;
  }

  return {
    thinkingBudget: normalizedModelId.endsWith("-low") ? 1000 : 10001,
    includeThoughts: true,
  };
}

function mergeThinkingConfigs(...configs: unknown[]): Record<string, unknown> | undefined {
  const merged: Record<string, unknown> = {};
  for (const config of configs) {
    const normalized = normalizeThinkingConfig(config);
    if (!normalized) {
      continue;
    }
    for (const [key, value] of Object.entries(normalized)) {
      if (value !== undefined) {
        merged[key] = value;
      }
    }
  }
  return Object.keys(merged).length > 0 ? merged : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function normalizeToolSchemaTypes(tools: unknown, toolMapper?: ToolMapper): void {
  if (!Array.isArray(tools)) return;

  const validSchemaKeys = new Set([
    "type", "description", "properties", "items",
    "required", "enum", "nullable", "format"
  ]);

  const sanitizeSchema = (obj: any) => {
    if (!obj || typeof obj !== "object") return;
    if (Array.isArray(obj)) {
      obj.forEach(sanitizeSchema);
      return;
    }

    if (!obj.type && (Array.isArray(obj.anyOf) || Array.isArray(obj.oneOf))) {
      const arr = obj.anyOf || obj.oneOf;
      if (arr.length > 0 && arr[0] && typeof arr[0].type === "string") {
        obj.type = arr[0].type;
        if (arr[0].items) {
          obj.items = arr[0].items;
        }
      } else {
        obj.type = "STRING";
      }
    }

    if (typeof obj.type === "string") {
      const t = obj.type.toLowerCase();
      if (["string", "number", "integer", "boolean", "array", "object"].includes(t)) {
        obj.type = t.toUpperCase();
      }
    }

    if (obj.properties && typeof obj.properties === "object") {
      Object.values(obj.properties).forEach(sanitizeSchema);
    }
    if (obj.items && typeof obj.items === "object") {
      sanitizeSchema(obj.items);
    }

    for (const key of Object.keys(obj)) {
      if (!validSchemaKeys.has(key)) {
        delete obj[key];
      }
    }
  };

  for (const tool of tools) {
    if (tool && Array.isArray(tool.functionDeclarations)) {
      for (const fn of tool.functionDeclarations) {
        if (fn && typeof fn.name === "string") {
          fn.name = toolMapper ? toolMapper.toGemini(fn.name) : sanitizeToolName(fn.name);
        }
        if (fn) {
          if (!fn.parameters) {
            fn.parameters = { type: "OBJECT", properties: {} };
          }
          sanitizeSchema(fn.parameters);
        }
      }
    }
  }
}

function normalizeToolConfig(requestPayload: Record<string, unknown>, toolMapper: ToolMapper): void {
  const toolConfig = (requestPayload.toolConfig ?? requestPayload.tool_config) as Record<string, unknown> | undefined;
  if (!toolConfig || typeof toolConfig !== "object") return;

  const fnCallingConfig = (toolConfig.functionCallingConfig ?? toolConfig.function_calling_config) as Record<string, unknown> | undefined;
  if (!fnCallingConfig || typeof fnCallingConfig !== "object") return;

  const allowedNames = (fnCallingConfig.allowedFunctionNames ?? fnCallingConfig.allowed_function_names) as string[] | undefined;
  if (Array.isArray(allowedNames)) {
    const mapped = allowedNames.map((name) => (typeof name === "string" ? toolMapper.toGemini(name) : name));
    if (fnCallingConfig.allowedFunctionNames) {
      fnCallingConfig.allowedFunctionNames = mapped;
    }
    if (fnCallingConfig.allowed_function_names) {
      fnCallingConfig.allowed_function_names = mapped;
    }
  }
}

function normalizeToolNamesInContents(contents: any[], toolMapper: ToolMapper): void {
  if (!Array.isArray(contents)) return;
  for (const msg of contents) {
    if (!msg || typeof msg !== "object" || !Array.isArray(msg.parts)) continue;
    for (const part of msg.parts) {
      if (!part || typeof part !== "object") continue;
      if (part.functionCall && typeof part.functionCall.name === "string") {
        part.functionCall.name = toolMapper.toGemini(part.functionCall.name);
      }
      if (part.functionResponse && typeof part.functionResponse.name === "string") {
        part.functionResponse.name = toolMapper.toGemini(part.functionResponse.name);
      }
    }
  }
}

function normalizeSystemInstruction(requestPayload: Record<string, unknown>): void {
  if ("system_instruction" in requestPayload) {
    requestPayload.systemInstruction = requestPayload.system_instruction;
    delete requestPayload.system_instruction;
  }
}

function normalizeCachedContent(requestPayload: Record<string, unknown>): void {
  const extraBody =
    requestPayload.extra_body && typeof requestPayload.extra_body === "object"
      ? (requestPayload.extra_body as Record<string, unknown>)
      : undefined;
  const cachedContentFromExtra = extraBody?.cached_content ?? extraBody?.cachedContent;
  const cachedContent =
    (requestPayload.cached_content as string | undefined) ??
    (requestPayload.cachedContent as string | undefined) ??
    (cachedContentFromExtra as string | undefined);

  if (cachedContent) {
    requestPayload.cachedContent = cachedContent;
  }

  delete requestPayload.cached_content;
  if (!extraBody) {
    return;
  }

  delete extraBody.cached_content;
  delete extraBody.cachedContent;
  if (Object.keys(extraBody).length === 0) {
    delete requestPayload.extra_body;
  }
}



export function ensureTrailingUserTurn(contents: any[]): any[] {
  if (!Array.isArray(contents) || contents.length === 0) {
    return contents;
  }
  const last = contents[contents.length - 1];
  if (last && (last.role === "model" || last.role === "assistant")) {
    return [...contents, { role: "user", parts: [{ text: "[Continue]" }] }];
  }
  return contents;
}

function normalizeContentsSequence(contents: any[]): any[] {
  const merged: any[] = [];
  for (const msg of contents) {
    if (!msg || !msg.role || !Array.isArray(msg.parts)) {
      continue;
    }
    const validParts = msg.parts.filter((p: any) => p != null);
    if (validParts.length === 0) {
      continue;
    }

    const last = merged[merged.length - 1];
    if (last && last.role === msg.role) {
      last.parts.push(...validParts);
    } else {
      merged.push({ ...msg, parts: validParts });
    }
  }
  return merged;
}

function injectMissingToolCallIds(contents: any[]): void {
  // Map of function name to array of missing IDs we generated for it
  const missingIdsByName = new Map<string, string[]>();

  for (const content of contents) {
    if (!content || typeof content !== "object" || !Array.isArray(content.parts)) {
      continue;
    }

    for (const part of content.parts) {
      if (!part || typeof part !== "object") {
        continue;
      }

      if (part.functionCall && typeof part.functionCall.name === "string") {
        if (!part.functionCall.id) {
          const generatedId = randomUUID();
          part.functionCall.id = generatedId;

          let ids = missingIdsByName.get(part.functionCall.name);
          if (!ids) {
            ids = [];
            missingIdsByName.set(part.functionCall.name, ids);
          }
          ids.push(generatedId);
        }
      }

      if (part.functionResponse && typeof part.functionResponse.name === "string") {
        if (!part.functionResponse.id) {
          const ids = missingIdsByName.get(part.functionResponse.name);
          if (ids && ids.length > 0) {
            part.functionResponse.id = ids.shift();
          } else {
             part.functionResponse.id = randomUUID();
          }
        }
      }
    }
  }
}

function applyLatestSignature(contents: any[], latestSig: string | undefined): void {
  // Collect all function call parts in chronological order
  const allFunctionParts: any[] = [];
  for (const content of contents) {
    if (content && typeof content === "object" && Array.isArray(content.parts)) {
      for (const part of content.parts) {
        if (part && typeof part === "object" && part.functionCall) {
          // If thoughtSignature was mistakenly placed inside functionCall, migrate or clean it up
          if (part.functionCall.thoughtSignature) {
            if (!part.thoughtSignature || part.thoughtSignature === "skip_thought_signature_validator") {
              part.thoughtSignature = part.functionCall.thoughtSignature;
            }
            delete part.functionCall.thoughtSignature;
          }
          allFunctionParts.push(part);
        }
      }
    }
  }

  // Only apply the latest signature to the VERY LAST function call part
  if (allFunctionParts.length > 0 && latestSig) {
    const lastPart = allFunctionParts[allFunctionParts.length - 1];
    if (!lastPart.thoughtSignature || lastPart.thoughtSignature === "skip_thought_signature_validator") {
      lastPart.thoughtSignature = latestSig;
    }
  }
}

function fixOrphanedFunctionResponses(contents: any[]): void {
  const validCallIds = new Set<string>();

  for (const content of contents) {
    if (!content || typeof content !== "object" || !Array.isArray(content.parts)) {
      continue;
    }
    for (const part of content.parts) {
      if (part && typeof part === "object" && part.functionCall && part.functionCall.id) {
        validCallIds.add(part.functionCall.id);
      }
    }
  }

  for (const content of contents) {
    if (!content || typeof content !== "object" || !Array.isArray(content.parts)) {
      continue;
    }
    for (const part of content.parts) {
      if (part && typeof part === "object" && part.functionResponse) {
        const id = part.functionResponse.id;
        if (!id || !validCallIds.has(id)) {
          const name = part.functionResponse.name || "unknown_tool";
          const responseObj = part.functionResponse.response || {};

          let responseStr = "";
          try {
            responseStr = typeof responseObj === "string" ? responseObj : JSON.stringify(responseObj);
          } catch (e) {
            responseStr = String(responseObj);
          }

          part.text = `[Orphaned Tool Response for ${name}]: ${responseStr}`;
          delete part.functionResponse;
        }
      }
    }
  }
}
